/**
 * Panel MRW para una OT — equivalente a GLSEnvioPanel pero contra MRW SAGEC.
 *
 * Flujo:
 *  1. Comprueba estado vía /api/mrw/health (si MRW_ENABLED=false → muestra aviso).
 *  2. Si la OT ya tiene `tracking_mrw_numero_envio` → muestra info + acciones
 *     (descargar etiqueta PDF, abrir seguimiento, cancelar envío).
 *  3. Si no → formulario para crear envío.
 *
 * Convive con GLSEnvioPanel hasta retirar GLS definitivamente.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, Package, Truck, ExternalLink, AlertTriangle, FileText, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/lib/api';

export default function MRWEnvioPanel({ orden, onRefresh }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => initialForm(orden));

  // MRW Points (entrega en franquicia) — solo para envíos
  const [usarPunto, setUsarPunto] = useState(false);
  const [puntos, setPuntos] = useState([]);
  const [puntosLoading, setPuntosLoading] = useState(false);

  // Tipo de operación: "envio" (Revix→cliente) | "recogida" (cliente→Revix)
  const [tipo, setTipo] = useState('envio');

  useEffect(() => {
    API.get('/mrw/health').then((r) => setHealth(r.data)).catch(() => setHealth({ enabled: false }));
  }, []);

  // Buscar puntos cuando el CP esté completo y el usuario active la opción
  const buscarPuntos = async () => {
    if (!form.codigo_postal || form.codigo_postal.length < 4) {
      toast.error('Introduce primero un código postal válido.');
      return;
    }
    setPuntosLoading(true);
    try {
      const { data } = await API.get('/mrw/points', { params: { cp: form.codigo_postal } });
      setPuntos(Array.isArray(data) ? data : []);
      if (!data?.length) toast.info('No se encontraron puntos MRW para ese CP.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error buscando puntos MRW');
    } finally {
      setPuntosLoading(false);
    }
  };

  const hasShipment = Boolean(orden?.tracking_mrw_numero_envio);

  // ── Crear envío o recogida ───────────────────────────────────────────
  const handleCrear = async () => {
    if (!form.nombre || !form.direccion || !form.codigo_postal || !form.poblacion) {
      toast.error('Faltan datos obligatorios (nombre, dirección, CP, población).');
      return;
    }
    setLoading(true);
    try {
      const { data } = await API.post('/mrw/envios', { ...form, tipo, orden_id: orden.id });
      const label = tipo === 'recogida' ? 'Recogida' : 'Envío';
      toast.success(`${label} MRW creado: ${data.numero_envio}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Error al crear ${tipo} MRW`);
    } finally {
      setLoading(false);
    }
  };

  // ── Cancelar ─────────────────────────────────────────────────────────
  const handleCancelar = async () => {
    if (!window.confirm(`¿Cancelar el envío ${orden.tracking_mrw_numero_envio}? Esta acción no se puede deshacer.`)) return;
    setLoading(true);
    try {
      await API.post(`/mrw/envios/${orden.tracking_mrw_numero_envio}/cancelar`);
      toast.success('Envío MRW cancelado');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al cancelar envío MRW');
    } finally {
      setLoading(false);
    }
  };

  // ── Descargar etiqueta ───────────────────────────────────────────────
  const handleEtiqueta = async () => {
    setLoading(true);
    try {
      const resp = await API.get(`/mrw/envios/${orden.tracking_mrw_numero_envio}/etiqueta`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al descargar etiqueta');
    } finally {
      setLoading(false);
    }
  };

  // ── Estado: cargando health ──────────────────────────────────────────
  if (!health) {
    return (
      <Card data-testid="mrw-panel-loading"><CardContent className="p-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Comprobando estado MRW…</CardContent></Card>
    );
  }

  // ── Estado: MRW deshabilitado ────────────────────────────────────────
  if (!health.enabled) {
    return (
      <Card data-testid="mrw-panel-disabled">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700">
            <Truck className="w-5 h-5" /> MRW (transporte)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="default" className="border-amber-200 bg-amber-50">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertTitle>Integración MRW no configurada</AlertTitle>
            <AlertDescription>
              MRW está {' '}<Badge variant="outline">deshabilitada</Badge>. Para activarla, configura las variables{' '}
              <code className="text-xs bg-muted px-1 rounded">MRW_ENABLED=true</code>{' '}+{' '}
              <code className="text-xs bg-muted px-1 rounded">MRW_CODIGO_FRANQUICIA</code> + abonado/usuario/password en{' '}
              <code className="text-xs bg-muted px-1 rounded">backend/.env</code>.
              Mientras tanto sigue usándose el panel GLS de esta OT.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // ── Estado: envío YA creado ──────────────────────────────────────────
  if (hasShipment) {
    const tipoExistente = orden.tracking_mrw_tipo || 'envio';
    const esRecogidaExistente = tipoExistente === 'recogida';
    return (
      <Card data-testid="mrw-panel-shipment">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-700">
            <Package className="w-5 h-5" />
            {esRecogidaExistente ? 'Recogida MRW programada' : 'Envío MRW'}
            <Badge variant="outline" className={esRecogidaExistente ? 'border-amber-400 text-amber-700' : 'border-blue-400 text-blue-700'}>
              {esRecogidaExistente ? '🚚 Cliente → Revix' : '📦 Revix → Cliente'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <KV label="Número MRW">{orden.tracking_mrw_numero_envio}</KV>
            <KV label="Tipo">{esRecogidaExistente ? 'Recogida' : 'Envío'}</KV>
            <KV label="Estado">{orden.tracking_mrw_estado || 'creado'}</KV>
            <KV label="Creado">{(orden.tracking_mrw_fecha_creacion || '').slice(0, 19).replace('T', ' ')}</KV>
            <KV label="Ambiente">{health.ambiente}</KV>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button onClick={handleEtiqueta} disabled={loading} variant="outline" data-testid="btn-mrw-etiqueta">
              <FileText className="w-4 h-4 mr-1" /> Descargar etiqueta
            </Button>
            {orden.tracking_mrw_url_seguimiento && (
              <a href={orden.tracking_mrw_url_seguimiento} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="btn-mrw-seguimiento">
                  <ExternalLink className="w-4 h-4 mr-1" /> Seguimiento
                </Button>
              </a>
            )}
            <Button onClick={handleCancelar} disabled={loading || orden.tracking_mrw_estado === 'cancelado'} variant="destructive" data-testid="btn-mrw-cancelar">
              <XCircle className="w-4 h-4 mr-1" /> Cancelar envío
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Estado: sin envío → formulario ───────────────────────────────────
  const esRecogida = tipo === 'recogida';
  return (
    <Card data-testid="mrw-panel-form">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-700">
          <Truck className="w-5 h-5" /> {esRecogida ? 'Programar recogida MRW' : 'Crear envío MRW'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {/* Selector tipo operación */}
        <div className="rounded-lg border bg-card p-3 flex flex-col sm:flex-row gap-2" data-testid="mrw-tipo-selector">
          <button
            type="button"
            onClick={() => setTipo('envio')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
              !esRecogida ? 'bg-blue-600 text-white border-blue-600' : 'bg-card text-foreground hover:bg-muted'
            }`}
            data-testid="btn-tipo-envio"
          >
            📦 Envío (Revix → cliente)
            <div className="text-[10px] font-normal opacity-80">Devolver móvil reparado</div>
          </button>
          <button
            type="button"
            onClick={() => {
              setTipo('recogida');
              setUsarPunto(false);
              setForm((f) => ({ ...f, codigo_punto_entrega: '' }));
            }}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
              esRecogida ? 'bg-amber-600 text-white border-amber-600' : 'bg-card text-foreground hover:bg-muted'
            }`}
            data-testid="btn-tipo-recogida"
          >
            🚚 Recogida (cliente → Revix)
            <div className="text-[10px] font-normal opacity-80">Recoger móvil averiado</div>
          </button>
        </div>

        <Alert variant="default" className={esRecogida ? 'border-amber-300 bg-amber-50' : 'border-blue-200 bg-blue-50/50'}>
          <AlertDescription className="text-xs">
            {esRecogida
              ? <>El mensajero pasará a recoger el paquete en el <strong>domicilio del cliente</strong>. La entrega se hará automáticamente en el taller Revix (configurado en <code className="bg-amber-100 px-1 rounded">MRW_REMITENTE_*</code>).</>
              : <>El paquete sale del taller Revix con destino al <strong>cliente final</strong>. Puedes opcionalmente entregar en un MRW Point.</>
            }
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Nombre destinatario *" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} />
          <Field label="NIF" value={form.nif} onChange={(v) => setForm({ ...form, nif: v })} />
          <Field label="Dirección *" value={form.direccion} onChange={(v) => setForm({ ...form, direccion: v })} />
          <Field label="Número" value={form.numero} onChange={(v) => setForm({ ...form, numero: v })} />
          <Field label="Resto (piso, puerta)" value={form.resto_direccion} onChange={(v) => setForm({ ...form, resto_direccion: v })} />
          <Field label="Código postal *" value={form.codigo_postal} onChange={(v) => setForm({ ...form, codigo_postal: v })} />
          <Field label="Población *" value={form.poblacion} onChange={(v) => setForm({ ...form, poblacion: v })} />
          <Field label="Teléfono" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} />
          <Field label="Bultos" type="number" value={form.bultos} onChange={(v) => setForm({ ...form, bultos: Number(v) || 1 })} />
          <Field label="Peso (kg)" type="number" value={form.peso_kg} onChange={(v) => setForm({ ...form, peso_kg: Number(v) || 1 })} />
        </div>

        {/* Entrega en MRW Point — solo para envíos */}
        {!esRecogida && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="mrw-points-box">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={usarPunto}
              onChange={(e) => {
                setUsarPunto(e.target.checked);
                if (!e.target.checked) setForm({ ...form, codigo_punto_entrega: '' });
              }}
              data-testid="check-usar-punto"
            />
            <span className="font-medium">Entregar en un MRW Point (franquicia / corner / receptoría)</span>
          </label>
          {usarPunto && (
            <div className="space-y-2 pl-6">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">CP destino</Label>
                  <Input
                    value={form.codigo_postal}
                    onChange={(e) => setForm({ ...form, codigo_postal: e.target.value })}
                    placeholder="14001"
                  />
                </div>
                <Button onClick={buscarPuntos} disabled={puntosLoading} variant="outline" data-testid="btn-buscar-puntos">
                  {puntosLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Buscar puntos
                </Button>
              </div>
              {puntos.length > 0 && (
                <div className="space-y-1 max-h-[260px] overflow-y-auto border rounded p-2 bg-card">
                  {puntos.map((p) => (
                    <label
                      key={p.codigo}
                      className={`flex items-start gap-2 p-2 rounded cursor-pointer text-xs ${
                        form.codigo_punto_entrega === p.codigo ? 'bg-blue-50 border border-blue-300' : 'hover:bg-muted/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="mrw-point"
                        checked={form.codigo_punto_entrega === p.codigo}
                        onChange={() => setForm({ ...form, codigo_punto_entrega: p.codigo })}
                        className="mt-1"
                        data-testid={`radio-punto-${p.codigo}`}
                      />
                      <div className="flex-1">
                        <div className="font-medium">
                          {p.nombre || p.codigo}
                          {p.tipo && <Badge variant="outline" className="ml-1 text-[10px] h-4">{p.tipo}</Badge>}
                        </div>
                        <div className="text-muted-foreground">
                          {p.direccion} · {p.cp} {p.poblacion}{p.provincia ? ` (${p.provincia})` : ''}
                        </div>
                        {p.horario && <div className="text-[10px] text-muted-foreground italic">{p.horario}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {form.codigo_punto_entrega && (
                <div className="text-xs text-green-700">
                  Punto seleccionado: <code className="bg-green-50 px-1 rounded">{form.codigo_punto_entrega}</code>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        <div className="flex justify-end pt-3 border-t">
          <Button
            onClick={handleCrear}
            disabled={loading}
            className={esRecogida ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}
            data-testid="btn-mrw-crear-envio"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
            {esRecogida ? 'Programar recogida' : 'Crear envío'} MRW
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────── Helpers UI ──────────────────────
function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-medium">{children || '—'}</div>
    </div>
  );
}

function initialForm(orden) {
  // Pre-rellenar con el cliente de la OT cuando exista.
  const c = orden?.cliente || {};
  return {
    nombre: orden?.cliente_nombre || c.nombre || '',
    nif: c.nif || '',
    direccion: c.direccion || '',
    numero: c.numero || '',
    resto_direccion: c.resto || '',
    codigo_postal: c.codigo_postal || c.cp || '',
    poblacion: c.poblacion || c.ciudad || '',
    pais: 'ES',
    telefono: c.telefono || orden?.telefono || '',
    codigo_servicio: '0200',
    bultos: 1,
    peso_kg: 1,
    referencia: orden?.numero_orden || '',
    tipo_via: 'CL',
    entrega_sabado: 'N',
    codigo_punto_entrega: '',
  };
}
